import { useTranslation } from 'react-i18next';
import { StepListStep } from '@src/components/StepsList';
import { useToast } from '@src/hooks/useToast';
import { logError } from '@src/services/error-logger';

export interface IntakeFlowStep<CategoryType extends string, ItemType> {
  /**
   * The key of the step. This is used to identify the step in the URL.
   */
  key: string;
  /**
   * The category of the step. This is used to group steps together in the sidebar.
   */
  category: CategoryType;
  /**
   * A function that returns true if the step is completed.
   */
  isStepCompleted?: (item: ItemType) => boolean;
  /**
   * A function that returns true if the step should be included in the flow.
   *
   * If it should be included for new items, do not provide a function.
   */
  shouldIncludeInFlow?: (order: ItemType) => boolean;
  /**
   * This step can only be accessed if it's a new item.
   */
  onlyNew?: boolean;
}

export interface UseStepFlowOptions<
  CategoryTypes extends readonly string[],
  ItemType,
> {
  /**
   * The categories of the steps in order.
   */
  stepCategories: CategoryTypes;
  /**
   * The steps of the flow.
   */
  steps: IntakeFlowStep<CategoryTypes[number], ItemType>[];
  /**
   * The key of the current step.
   */
  currentStepKey?: string;
  /**
   * The item that is being edited if available.
   */
  item?: ItemType;
  /**
   * True if we are creating a new item and no item should be expected.
   */
  isNew?: boolean;
  /**
   * A function that returns the URL to a step.
   */
  makeUrlToStep: (stepKey: string, item: ItemType | undefined) => string;
}

export interface UseStepFlowLoadingResult {
  type: 'loading';
}

export interface UseStepFlowStepResult<ItemType> {
  type: 'step';
  categoryItems: StepListStep[];
  currentCategoryName: string;
  previousUrl: string | null;
  getNextUrl: (order?: ItemType) => string;
}

export interface UseStepFlowRedirectResult {
  type: 'redirect';
  to: string;
}

export type UseStepFlowResult<ItemType> =
  | UseStepFlowLoadingResult
  | UseStepFlowStepResult<ItemType>
  | UseStepFlowRedirectResult;

/** Checks if the step should be included in the flow */
function isStepIncluded<CategoryType extends string, ItemType>(
  step: IntakeFlowStep<CategoryType, ItemType>,
  item?: ItemType,
): boolean {
  if (step.onlyNew && item) {
    return false;
  }
  if (step.shouldIncludeInFlow) {
    if (!item) {
      return false;
    }
    return !step.shouldIncludeInFlow || step.shouldIncludeInFlow(item);
  }
  return true;
}

/**
 * Gets an array of whether each step is accessible in the flow matching the index of the provided steps.
 */
function getStepAccessibilities<CategoryType extends string, ItemType>(
  steps: IntakeFlowStep<CategoryType, ItemType>[],
  item?: ItemType,
): boolean[] {
  interface StatusAccumulator {
    statuses: boolean[];
    isPreviousIncomplete: boolean;
  }
  const result = steps.reduce<StatusAccumulator>(
    ({ statuses, isPreviousIncomplete }, step) => {
      if (!isStepIncluded(step, item)) {
        return {
          statuses: [...statuses, false],
          isPreviousIncomplete,
        };
      }
      // if any previous step was incomplete, mark all following steps as not ready
      if (isPreviousIncomplete) {
        return { statuses: [...statuses, false], isPreviousIncomplete };
      }
      // if step is completed, mark it as completed
      const isCompleted = step.isStepCompleted
        ? !!item && step.isStepCompleted(item)
        : true;
      return {
        statuses: [...statuses, true],
        isPreviousIncomplete: !isCompleted,
      };
    },
    { statuses: [], isPreviousIncomplete: false },
  );
  return result.statuses;
}

function getLastAccessibleStepKey<CategoryType extends string, ItemType>(
  steps: IntakeFlowStep<CategoryType, ItemType>[],
  stepAccessibilities: boolean[],
): string | null {
  for (let i = stepAccessibilities.length - 1; i >= 0; i -= 1) {
    if (stepAccessibilities[i]) {
      return steps[i].key;
    }
  }
  return null;
}

export function useStepFlow<CategoryTypes extends readonly string[], ItemType>({
  currentStepKey,
  makeUrlToStep,
  item,
  isNew,
  steps,
  stepCategories,
}: UseStepFlowOptions<CategoryTypes, ItemType>): UseStepFlowResult<ItemType> {
  const toast = useToast();
  const { t: manualTranslate, i18n } = useTranslation();

  // direct the user to the appropriate step for where they are if we don't have an explicit step
  // or they should not access the step
  const currentStep = steps.find((step) => step.key === currentStepKey);
  const currentStepIdx = currentStep ? steps.indexOf(currentStep) : -1;

  const stepStatuses = getStepAccessibilities(steps, item);

  if (!currentStepKey || !currentStep || !stepStatuses[currentStepIdx]) {
    if (isNew) {
      return {
        type: 'redirect',
        to: makeUrlToStep(steps[0].key, item),
      };
    }
    if (!item) {
      return { type: 'loading' };
    }

    // find the furthest valid step
    const furthestValidStepKey = getLastAccessibleStepKey(steps, stepStatuses);

    if (!furthestValidStepKey) {
      toast.error(
        'Sorry, something went wrong. Please try again or contact Bordr support.',
      );
      logError(new Error(`No accessible step was found in flow`));
    }

    return {
      type: 'redirect',
      to: furthestValidStepKey
        ? makeUrlToStep(furthestValidStepKey, item)
        : '/',
    };
  }

  // get previous step
  const previousStepKey = getLastAccessibleStepKey(
    steps.slice(0, currentStepIdx),
    stepStatuses.slice(0, currentStepIdx),
  );

  // generate sidebar steps
  const sidebarCategoryIdx = stepCategories.indexOf(currentStep.category);
  const categoryItems: StepListStep[] = stepCategories.map((category, i) => {
    let status: StepListStep['status'] = 'complete';
    if (i === sidebarCategoryIdx) {
      status = 'current';
    } else if (i > sidebarCategoryIdx) {
      status = 'upcoming';
    }

    // find first valid step in each category
    const firstValidStep = steps.find(
      (step, idx) => step.category === category && stepStatuses[idx],
    );
    const href =
      (firstValidStep && makeUrlToStep(firstValidStep.key, item)) || '#';

    return {
      name: i18n.exists(category) ? manualTranslate(category) : category,
      href,
      status,
    };
  });

  return {
    type: 'step',
    categoryItems,
    currentCategoryName: categoryItems[sidebarCategoryIdx].name,
    previousUrl: previousStepKey && makeUrlToStep(previousStepKey, item),
    getNextUrl: (newItem) => {
      const nextStepIdx = currentStepIdx + 1;
      if (nextStepIdx >= steps.length) {
        // The final step should handle the next URL manually showing a toast if needed
        logError(
          new Error(`No next step found for step ${currentStep.key} in flow`),
        );
        return '/';
      }

      const nextSteps = steps.slice(nextStepIdx);
      const newStepStatuses = getStepAccessibilities(steps, newItem).slice(
        nextStepIdx,
      );

      const nextStep = nextSteps.find((step, idx) => newStepStatuses[idx]);
      if (!nextStep) {
        // This shouldn't really happen
        toast.error(
          'Sorry, something went wrong. Please try again or contact Bordr support.',
        );
        logError(
          new Error(
            `No next step that was acesssible found for step ${currentStep.key} in flow`,
          ),
        );
        return '/';
      }
      return makeUrlToStep(nextStep.key, newItem);
    },
  };
}
